import { useMemo } from 'react'
import { type CascadingMenuOption } from '@opallabs/concepts'
import { RootHeader, SubmenuHeader } from '../MenuHeaders'
import { useCascadingMenu } from '../Root'
import { Item } from './Item'
import { NoResultsItem } from './NoResultsItem'
import * as styles from './styles'

interface SearchableItemsProps {
  emptyMessage?: string
  /**
   * This should be supplied when CascadingMenu is used
   * in conjunction with a combobox to support the
   * aria-controls attributes. See CascadingSingleSelect
   */
  itemListId?: string
  label?: string
  multiselectable: boolean
}

export const SearchableItems: React.FC<SearchableItemsProps> = ({
  emptyMessage = 'No results',
  itemListId,
  label,
  multiselectable,
}) => {
  const {
    descendMenuLevel,
    focusedItemIndex,
    searchResults,
    onSelect,
    searchValue,
    selectedItem,
    setSelectedItem,
    submenuTitle,
  } = useCascadingMenu()

  const activeDescendant = useMemo(() => {
    if (focusedItemIndex === -1) return
    if (!searchResults.length && focusedItemIndex === 0)
      return styles.NO_RESULTS_ID

    const focusedItem = searchResults[focusedItemIndex]

    if (!focusedItem) return

    return styles.composeItemId(focusedItem.value)
  }, [focusedItemIndex, searchResults])

  const handleSelect = (item: CascadingMenuOption) => {
    if (item.disableSelection) return

    setSelectedItem(item)
    onSelect(item)
  }

  return (
    <>
      <RootHeader />
      {submenuTitle && <SubmenuHeader title={submenuTitle} />}

      <ul
        css={styles.itemsContainer}
        aria-activedescendant={activeDescendant}
        aria-labelledby={
          !searchValue && label ? styles.MENU_LABEL_ID : undefined
        }
        aria-label={
          searchValue && !label
            ? (submenuTitle ?? 'Menu Search Results')
            : searchValue && label
              ? label
              : undefined
        }
        aria-multiselectable={multiselectable}
        id={itemListId}
        role="tree"
        tabIndex={-1}
      >
        {!searchValue && label && (
          <div id={styles.MENU_LABEL_ID} css={styles.itemsLabel}>
            {label}
          </div>
        )}
        {searchResults.length > 0 ? (
          searchResults.map((item, index) => (
            <Item
              key={item.value}
              {...item}
              index={index}
              selected={
                // Changing `selectedItem` overrides possibly-stale `item.selected`
                selectedItem != null
                  ? item.value === selectedItem.value
                  : (item.selected ?? false)
              }
              onSelect={() => handleSelect(item)}
              onDescendMenu={() => descendMenuLevel(item)}
            />
          ))
        ) : (
          <NoResultsItem index={0} label={emptyMessage} />
        )}
      </ul>
    </>
  )
}
SearchableItems.displayName = 'Lapidary: CascadingMenu.SearchableItems'
